// ==============================================================================
// SATELLITE MACHINE ADMIN SERVICE
// ==============================================================================
// Service for satellite administration operations.
// Uses getApiInstance() for automatic token refresh handling.
// All operations require SYSADMIN role on the server.

import { getApiInstance } from "../../../api";

export interface SatelliteAdminResult {
  success: boolean;
  error?: string | null;
  plainToken?: string | null;
  satellite?: {
    id: string;
    status?: string;
    enabled?: boolean;
    authTokenLastFour?: string;
    authorizedAt?: string;
    suspendedAt?: string;
    suspensionReason?: string;
    authTokenRotatedAt?: string;
  } | null;
}

export interface SatelliteConnectionInfo {
  connected: boolean;
  connectedAt?: string | null;
  lastHeartbeat?: string | null;
  publicIp?: string | null;
  activeTasks?: number | null;
}

export interface ConnectedSatellite {
  id: string;
  name: string;
  machineCode: string;
  status: string;
  region?: string | null;
  satelliteType: string;
  enabled: boolean;
  lastHeartbeatAt?: string | null;
}

export const satelliteMachineAdminService = {
  /**
   * Authorize a pending satellite, allowing it to connect.
   */
  authorizeSatellite: async (
    id: string,
    accessToken: string,
    activityStore: any
  ): Promise<SatelliteAdminResult> => {
    const api = getApiInstance(accessToken, activityStore);
    const result = await api.authorizeSatellite({ id });
    return result.authorizeSatellite as SatelliteAdminResult;
  },

  /**
   * Suspend a satellite temporarily. Can be reactivated later.
   */
  suspendSatellite: async (
    id: string,
    reason: string,
    accessToken: string,
    activityStore: any
  ): Promise<SatelliteAdminResult> => {
    const api = getApiInstance(accessToken, activityStore);
    const result = await api.suspendSatellite({ id, reason });
    return result.suspendSatellite as SatelliteAdminResult;
  },

  /**
   * Reactivate a suspended satellite.
   */
  reactivateSatellite: async (
    id: string,
    accessToken: string,
    activityStore: any
  ): Promise<SatelliteAdminResult> => {
    const api = getApiInstance(accessToken, activityStore);
    const result = await api.reactivateSatellite({ id });
    return result.reactivateSatellite as SatelliteAdminResult;
  },

  /**
   * Revoke a satellite permanently. Cannot be undone.
   */
  revokeSatellite: async (
    id: string,
    reason: string,
    accessToken: string,
    activityStore: any
  ): Promise<SatelliteAdminResult> => {
    const api = getApiInstance(accessToken, activityStore);
    const result = await api.revokeSatellite({ id, reason });
    return result.revokeSatellite as SatelliteAdminResult;
  },

  /**
   * Toggle the enabled flag without changing status.
   */
  setSatelliteEnabled: async (
    id: string,
    enabled: boolean,
    accessToken: string,
    activityStore: any
  ): Promise<SatelliteAdminResult> => {
    const api = getApiInstance(accessToken, activityStore);
    const result = await api.setSatelliteEnabled({ id, enabled });
    return result.setSatelliteEnabled as SatelliteAdminResult;
  },

  /**
   * Rotate the satellite's authentication token.
   * Returns the new plainToken - only shown once.
   */
  rotateSatelliteToken: async (
    id: string,
    accessToken: string,
    activityStore: any
  ): Promise<SatelliteAdminResult> => {
    const api = getApiInstance(accessToken, activityStore);
    const result = await api.rotateSatelliteToken({ id });
    return result.rotateSatelliteToken as SatelliteAdminResult;
  },

  /**
   * Force disconnect a connected satellite.
   */
  disconnectSatellite: async (
    id: string,
    accessToken: string,
    activityStore: any
  ): Promise<SatelliteAdminResult> => {
    const api = getApiInstance(accessToken, activityStore);
    const result = await api.disconnectSatellite({ id });
    return result.disconnectSatellite as SatelliteAdminResult;
  },

  /**
   * Get connection info for a satellite.
   */
  getConnectionInfo: async (
    id: string,
    accessToken: string,
    activityStore: any
  ): Promise<SatelliteConnectionInfo | null> => {
    const api = getApiInstance(accessToken, activityStore);
    const result = await api.satelliteConnectionInfo({ id });
    return result.satelliteConnectionInfo as SatelliteConnectionInfo | null;
  },

  /**
   * List all connected satellites with their status.
   */
  getConnectedSatellites: async (
    accessToken: string,
    activityStore: any
  ): Promise<ConnectedSatellite[]> => {
    const api = getApiInstance(accessToken, activityStore);
    const result = await api.connectedSatellites();
    return (result.connectedSatellites || []) as ConnectedSatellite[];
  },
};
